// per docs/shape-uc27-alert-engine.md §Implementation plan §11
// per docs/caiet-uc-spec.md §UC27-02 §UC-ALRT-04
// The register follows the wireframe's definition list
// (wireframes/react/src/pages/AdminAlerte.tsx:3576-3646): a leading ActiuniMenu,
// the human name as the row's identity with the technical name and the subject on
// a metadata line beneath it, and a Șterge that is disabled — saying why — while a
// rule still points at the template.
//
// Editing lives at its own address (`alert-templates/:id/edit`,
// NotificationTemplateFormPage), as the wireframe's `sabloane/:id/edit` does.
// This page therefore lists, deletes, and hands over.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { TableColumn, TableRowData } from '@egov-moldova/mud/loader';
import { MudButton, MudIcon, MudModal, MudInfoBox, MudTable } from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { QueryState } from '../../components/QueryState/QueryState';
import {
  EmptyRegisterMessage,
  MetadataLine,
  onModalDismiss,
  onRowOpen,
  PageHeader,
  RouterLink,
  RowActionsMenu,
  useToast,
} from '@simsm/ui-shared';
import { AlertsSectionNav } from '../../components/AlertsSectionNav';
import { AlertDefinitionsMenu } from '../../components/AlertDefinitionsMenu';
import type { RuleDto } from './AlertRulesPage';
import { bodyPreview, localeLabelKey } from './notification-template-list';

interface TemplateDto {
  id: string;
  name: string;
  displayName: string;
  titleTpl: string;
  bodyTpl: string;
  locale: string;
  metadata: Record<string, unknown>;
}

export function NotificationTemplatesPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  // A Tolgee key, not a sentence: the service refuses with a code and the
  // administrator reads the refusal in their own language.
  const [deleteErrorKey, setDeleteErrorKey] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () =>
      apiClient
        .get<{ templates: TemplateDto[] }>('/admin/notification-templates')
        .then((r) => r.data.templates),
  });

  // Which templates a rule still points at. The gateway refuses the delete too —
  // there is no foreign key behind either of them — but a menu entry that is
  // simply gone, or one that fails on click, is a worse answer than one that is
  // there, disabled, and says why (wireframe `motiv`).
  const rules = useQuery({
    queryKey: ['alert-rules'],
    queryFn: () =>
      apiClient.get<{ rules: RuleDto[] }>('/admin/alert-rules').then((r) => r.data.rules),
  });

  const remove = useMutation({
    mutationFn: ({ id }: { id: string; name: string }) =>
      apiClient.delete(`/admin/notification-templates/${id}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
      setDeleteErrorKey(null);
      setDeleteTarget(null);
      toast({
        variant: 'info',
        title: t('alerts.toast.template.deleted'),
        text: variables.name,
        closeLabel: t('common.close'),
      });
    },
    onError: (e: unknown) => {
      setDeleteErrorKey(apiErrorKey(e, 'alerts.templates.error.delete_failed'));
    },
  });

  const rows = query.data ?? [];
  const usedByRules = (templateId: string): number =>
    (rules.data ?? []).filter((r) => r.templateId === templateId).length;

  // A locale the editor offers has a name; one that arrived by import is shown as stored.
  function localeName(locale: string): string {
    const key = localeLabelKey(locale);
    return key ? t(key) : locale;
  }

  function editorHref(templateId: string): string {
    return `/system-admin/alert-templates/${templateId}/edit`;
  }

  // The wireframe's `tbl zebra compact … leading-actions-table` (AdminAlerte.tsx
  // :3579) as AGE's own table, with the shared leading row-actions track
  // (docs/ui-shape.md §Shared leading row-actions column): a 44px content track
  // plus mud-table's 24px cell padding on each side, no visible heading.
  // The identity column after the actions carries the name and, beneath it, the title;
  // the language and a one-line preview of the body follow (TC-UC27-02-01 rezultat_ui,
  // BUG-1-017; docs/ui-shape-admin-aggregations-alerts.md §4.2, §12).
  const columns: TableColumn[] = [
    { key: 'actions', label: '', width: '76px' },
    { key: 'template', label: t('alerts.templates.column.template') },
    { key: 'locale', label: t('alerts.templates.column.locale'), width: '120px' },
    { key: 'body', label: t('alerts.templates.column.body_preview') },
  ];
  const tableRows: TableRowData[] = rows.map((tpl) => ({ id: tpl.id }));

  // An empty register keeps its table. The wireframe leaves the head, the
  // frame and the footer standing and puts the message in one centred cell
  // spanning every column (AdminAlerte.tsx:3738); a box in
  // place of the register hides what the register would even hold. Both
  // QueryState branches render this same node, because mud-table draws its
  // `empty` slot on its own once `rows` is [].
  const register = (
    <>
      <div className="simsm-alerts__register">
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={tableRows}
          rowStyle="zebra"
          disableSort={true}
          aria-label={t('alerts.section.templates')}
          // The whole row opens the template's editor, the menu's first entry.
          hoverable
          onMudRowClick={onRowOpen((index) => {
            const tpl = rows[index];
            if (tpl) navigate(editorHref(tpl.id));
          })}
        >
          <EmptyRegisterMessage>{t('alerts.templates.empty')}</EmptyRegisterMessage>
          <span slot="header-cell-actions" className="visually-hidden">
            {t('alerts.column.actions')}
          </span>
          {rows.map((tpl, index) => {
            const used = usedByRules(tpl.id);
            return [
              <span
                key={`actions-${tpl.id}`}
                slot={`cell-actions-${index}`}
                className="simsm-alerts__cell simsm-alerts__cell--actions"
              >
                <RowActionsMenu
                  label={t('alerts.action.row_menu', { name: tpl.displayName })}
                  actions={[
                    {
                      label: t('alerts.action.edit'),
                      onSelect: () => navigate(editorHref(tpl.id)),
                    },
                    {
                      label: t('alerts.action.delete'),
                      danger: true,
                      separator: true,
                      disabled: used > 0,
                      reason: t('alerts.templates.in_use'),
                      onSelect: () => {
                        setDeleteErrorKey(null);
                        setDeleteTarget({ id: tpl.id, name: tpl.displayName });
                      },
                    },
                  ]}
                />
              </span>,
              <span
                key={`template-${tpl.id}`}
                slot={`cell-template-${index}`}
                className="simsm-alerts__cell-name simsm-alerts__cell-name--wrap"
              >
                {/* A real link, because the editor is a real address: it opens
                    in a new tab, it can be copied, and the browser shows where
                    it goes. While the editor was a dialog this had to be a
                    callback with no href, which is what the wireframe's
                    `sabloane/:id/edit` route existed to avoid. */}
                <RouterLink to={editorHref(tpl.id)} size="sm" variant="strict" underline="hover">
                  {tpl.displayName}
                </RouterLink>
                <MetadataLine
                  wrap
                  items={[
                    {
                      label: t('alerts.templates.column.title'),
                      value: tpl.titleTpl,
                      title: tpl.titleTpl,
                    },
                  ]}
                />
              </span>,
              <span
                key={`locale-${tpl.id}`}
                slot={`cell-locale-${index}`}
                className="simsm-alerts__cell"
              >
                {localeName(tpl.locale)}
              </span>,
              <span
                key={`body-${tpl.id}`}
                slot={`cell-body-${index}`}
                className="simsm-alerts__cell simsm-alerts__cell--body"
              >
                <span className="simsm-alerts__clamp" title={tpl.bodyTpl}>
                  {bodyPreview(tpl.bodyTpl)}
                </span>
              </span>,
            ];
          })}
        </MudTable>
      </div>
      {/* The register says how many rows it is showing, as the alert register
          and the trigger history beside it do and as the wireframe does on
          every one of its five tabs. */}
      <div className="simsm-alerts__table-footer">
        <span className="simsm-alerts__muted-text">
          {t(rows.length === 1 ? 'alerts.templates.count_one' : 'alerts.templates.count_other', {
            count: rows.length,
          })}
        </span>
      </div>
    </>
  );

  return (
    <section className="simsm-page simsm-alerts">
      <PageHeader
        title={t('alerts.section_title')}
        lede={t('alerts.section_lede')}
        actions={
          <>
            {/* The wireframe carries the definitions menu on every definition tab,
                not only the first: the export/import bundle covers all four object
                types whichever tab it is started from. */}
            <AlertDefinitionsMenu />
            <MudButton
              shape="circular"
              variant="primary"
              size="md"
              onClick={() => navigate('/system-admin/alert-templates/new')}
            >
              <MudIcon slot="icon-start" name="plus-large" size={16} />
              {t('alerts.templates.action.new')}
            </MudButton>
          </>
        }
      />

      <AlertsSectionNav />

      <QueryState query={query} isEmpty={rows.length === 0} empty={register}>
        {register}
      </QueryState>

      <MudModal
        closeLabel={t('common.close')}
        open={deleteTarget !== null}
        size="sm"
        titleText={t('alerts.confirm.delete.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied to
        // every modal uniformly; a no-op here until a descendant emits its own mudClose.
        onMudClose={onModalDismiss(() => {
          setDeleteTarget(null);
          setDeleteErrorKey(null);
        })}
      >
        {deleteErrorKey ? (
          <MudInfoBox emphasis="strong" role="alert" variant="error">
            {t(deleteErrorKey)}
          </MudInfoBox>
        ) : null}
        {deleteTarget ? (
          <p className="simsm-alerts__confirm-body">
            {t('alerts.confirm.delete.body', { name: deleteTarget.name })}
          </p>
        ) : null}
        <div slot="actions" className="simsm-alerts__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => {
              setDeleteTarget(null);
              setDeleteErrorKey(null);
            }}
          >
            {t('alerts.confirm.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="destructive"
            size="md"
            disabled={remove.isPending}
            onClick={() => {
              if (deleteTarget) {
                setDeleteErrorKey(null);
                remove.mutate({ id: deleteTarget.id, name: deleteTarget.name });
              }
            }}
          >
            {t('alerts.confirm.action.confirm')}
          </MudButton>
        </div>
      </MudModal>
    </section>
  );
}
